// crewbit-admin/src/components/money/QuickBillPanel.tsx
//
// §Money.StubActionSweepCompletion Phase 3. Centered card modal port of the
// mobile QuickBillSheet flow. Walk-in client + line-item shortcut + amount →
// create_invoice (is_sales_receipt=true) → mark_paid_manually (cash on the spot).
// Operator's contact field is heuristically split (contains '@' → email, else phone).

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { palette, spacing, transition, typography } from '../../styles/tokens';
import { MoneyInput } from '../common/MoneyInput';
import { canonicalMoney, parseMoney, isPositive } from '../../lib/money';
import { createInvoice, markPaidManually } from '../../lib/invoiceMutations';
import { lookupErrorCopy } from '../../lib/errorMap';
import { useActiveBusinessStore } from '../../hooks/useActiveBusiness';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onResult: (ok: boolean, message: string) => void;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function QuickBillPanel({ isOpen, onClose, onResult }: Props) {
  const navigate = useNavigate();
  const businessId = useActiveBusinessStore((s) => s.activeBusinessId);

  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [clientName, setClientName] = useState('');
  const [clientContact, setClientContact] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState(canonicalMoney(0));
  const [dueDate, setDueDate] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const parsedAmountStr = parseMoney(amount);
  const parsedAmount = Number(parsedAmountStr);
  const valid = clientName.trim().length > 0
    && description.trim().length > 0
    && isPositive(parsedAmountStr);

  const reset = () => {
    setClientName('');
    setClientContact('');
    setDescription('');
    setAmount(canonicalMoney(0));
    setDueDate('');
    setIdempotencyKey(crypto.randomUUID());
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const onConfirm = async () => {
    if (!valid || !businessId) return;
    setLoading(true);
    const trimmedContact = clientContact.trim();
    const contactIsEmail = trimmedContact.includes('@');

    const createResult = await createInvoice({
      businessId,
      clientId: null,
      lineItems: [{
        description: description.trim(),
        quantity: 1,
        unit_price: parsedAmount,
        amount: parsedAmount,
      }],
      isSalesReceipt: true,
      idempotencyKey,
      issueDate: todayIso(),
      dueDate: dueDate || null,
      notes: `Quick Bill: ${clientName.trim()}${trimmedContact ? ` (${trimmedContact})` : ''}`,
      taxRate: 0,
      clientName: clientName.trim(),
      clientEmail: contactIsEmail ? trimmedContact : null,
      clientPhone: !contactIsEmail && trimmedContact ? trimmedContact : null,
    });

    if (!createResult.success) {
      setLoading(false);
      onResult(false, lookupErrorCopy('create_invoice', createResult.error.code));
      return;
    }

    // Cash-on-the-spot mark paid (Quick Bill semantic).
    const payResult = await markPaidManually(createResult.data.invoice_id, {
      amount: parsedAmount,
      method: 'cash',
      receivedAt: new Date().toISOString(),
      notes: `Quick Bill — ${clientName.trim()}`,
    });

    setLoading(false);

    if (!payResult.success) {
      onResult(false, `Invoice ${createResult.data.invoice_number} created but mark-paid failed: ${payResult.error.message ?? 'unknown error'}. Mark paid from the detail panel.`);
    } else {
      onResult(true, `Sales receipt ${createResult.data.invoice_number} recorded.`);
    }

    reset();
    onClose();
    void navigate(`/money/invoices?invoice_id=${createResult.data.invoice_id}`);
  };

  return (
    <>
      <div onClick={handleClose} style={{
        position: 'fixed', inset: 0, zIndex: 70,
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%', maxWidth: 460, zIndex: 71,
        background: palette.white, borderRadius: 16,
        boxShadow: '0 16px 64px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.08)',
        padding: 28,
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: palette.accentWash,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: spacing.md,
        }}>
          <Zap size={22} color={palette.accent} strokeWidth={1.8} />
        </div>

        <div style={{ fontSize: 16, fontWeight: 700, color: palette.g800, marginBottom: 6 }}>
          Quick bill
        </div>
        <div style={{ fontSize: 14, color: palette.g500, lineHeight: 1.5, marginBottom: spacing.md }}>
          One-line invoice for cash-on-the-spot sales. Records as a sales receipt and marks paid.
        </div>

        <div style={{ display: 'grid', gap: spacing.sm }}>
          <Field label="Client name" required>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value.slice(0, 200))}
              placeholder="Walk-in or client name"
              style={inputStyle}
              autoFocus
            />
          </Field>

          <Field label="Phone or email">
            <input
              type="text"
              value={clientContact}
              onChange={(e) => setClientContact(e.target.value.slice(0, 200))}
              placeholder="Optional"
              style={inputStyle}
            />
          </Field>

          <Field label="What's it for" required>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 200))}
              placeholder="Description"
              style={inputStyle}
            />
          </Field>

          <Field label="How much" required>
            <MoneyInput value={amount} onChange={setAmount} ariaLabel="Quick bill amount" />
          </Field>

          <Field label="Due date">
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              style={inputStyle}
            />
          </Field>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: spacing.md }}>
          <button onClick={handleClose} disabled={loading} style={{
            padding: '9px 22px', borderRadius: 20,
            border: `1px solid ${palette.g200}`, background: palette.white,
            fontSize: 13.5, fontWeight: 500, color: palette.g600,
            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            transition: `background ${transition.fast}`,
          }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading || !valid} style={{
            padding: '9px 22px', borderRadius: 20, border: 'none',
            background: (loading || !valid) ? palette.g300 : palette.accent,
            color: palette.white, fontSize: 13.5, fontWeight: 600,
            cursor: (loading || !valid) ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}>{loading ? 'Recording…' : 'Record sale'}</button>
        </div>
      </div>
    </>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: spacing.sm, borderRadius: 8,
  border: `1px solid ${palette.g200}`, fontFamily: 'inherit',
  fontSize: typography.size.caption, color: palette.g800,
  boxSizing: 'border-box', background: palette.white,
};

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: typography.size.caption, color: palette.g700, marginBottom: spacing.xs }}>
        {label}{required && <span style={{ color: palette.red, marginLeft: 2 }}>*</span>}
      </div>
      {children}
    </label>
  );
}
